import { useMemo, useState } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { formatAxisTime, formatBucket, formatCompact, DASH } from "../format";
import type { MonitoringWindow } from "../types";

export interface ChartSeries {
  /** data key of the series; no dots (recharts reads "a.b" as a nested path and draws nothing) */
  key: string;
  label: string;
  color: string;
  values: (number | null | undefined)[];
  type?: "line" | "area" | "bar";
  stackId?: string;
  dashed?: boolean;
  /** hidden until the legend entry is clicked */
  hidden?: boolean;
  /** draw as steps (gauges like "healthy pods") */
  step?: boolean;
}

interface TimeSeriesChartProps {
  t: number[];
  stepMs: number;
  window: MonitoringWindow;
  series: ChartSeries[];
  height?: number;
  /** value format of the tooltip */
  format?: (value: number) => string;
  /** value format of the y axis ticks (defaults to a compact number) */
  tickFormat?: (value: number) => string;
  yMax?: number | "auto";
  referenceLines?: { y: number; label?: string; color?: string }[];
  emptyText?: string;
  className?: string;
  /** whole-number axis (counts) */
  integer?: boolean;
  /** width of the y axis labels (latency labels like "800 ms" need more than counts) */
  yWidth?: number;
  /** a 0 is a real value (gauges like "healthy pods"), not "nothing happened": no empty text over zeros */
  zeroIsData?: boolean;
}

/**
 * One time series chart of the board: bars, lines and areas over the buckets of a window, a crosshair
 * tooltip with every series of the bucket, and a clickable legend (shown for two or more series).
 * Missing buckets stay gaps instead of being drawn as zero.
 */
export function TimeSeriesChart({
  t,
  stepMs,
  window,
  series,
  height = 200,
  format = (v) => formatCompact(v),
  tickFormat = (v) => formatCompact(v),
  yMax = "auto",
  referenceLines,
  emptyText,
  className,
  integer = false,
  yWidth,
  zeroIsData = false,
}: TimeSeriesChartProps) {
  const { t: tr } = useLocalization();
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(series.filter((s) => s.hidden).map((s) => s.key)),
  );

  const rows = useMemo(
    () =>
      t.map((ts, i) => {
        const row: Record<string, number | null> = { t: ts };
        for (const s of series) {
          const value = s.values[i];
          row[s.key] = typeof value === "number" && Number.isFinite(value) ? value : null;
        }
        return row;
      }),
    [t, series],
  );

  const config = useMemo<ChartConfig>(
    () => Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }])),
    [series],
  );

  const visible = series.filter((s) => !hidden.has(s.key));
  // labels like "1.2 s" / "800 ms" are wider than plain counts
  const axisWidth =
    yWidth ?? Math.min(64, Math.max(34, 10 + 6 * Math.max(...[800, 1234, 12000].map((v) => tickFormat(v).length))));
  const hasData = visible.some((s) => s.values.some((v) => typeof v === "number" && (zeroIsData || v !== 0)));

  // the last bar of each stack gets the rounded data end
  const stackTops = new Map<string, string>();
  for (const s of visible) if (s.type === "bar") stackTops.set(s.stackId ?? s.key, s.key);

  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      {series.length > 1 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1" role="group" aria-label={tr("monitoring.chart.legend")}>
          {series.map((s) => {
            const off = hidden.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggle(s.key)}
                aria-pressed={!off}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground transition-opacity hover:text-foreground",
                  off && "opacity-40",
                )}
              >
                <Swatch color={s.color} type={s.type} dashed={s.dashed} />
                {s.label}
              </button>
            );
          })}
        </div>
      )}
      <div className="relative">
        <ChartContainer config={config} className="aspect-auto w-full" style={{ height }}>
          <ComposedChart data={rows} margin={{ top: 6, right: 8, bottom: 0, left: 0 }} barCategoryGap="18%">
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
            <XAxis
              dataKey="t"
              tickLine={false}
              axisLine={false}
              tickMargin={6}
              minTickGap={28}
              interval="preserveStartEnd"
              tickFormatter={(value: number) => formatAxisTime(value, window)}
              className="text-[10px]"
            />
            <YAxis
              width={axisWidth}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              allowDecimals={!integer}
              domain={[0, yMax]}
              tickFormatter={tickFormat}
              className="text-[10px]"
            />
            <ChartTooltip
              cursor={{ className: "fill-muted/40 stroke-border" }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="min-w-[10rem] rounded-md border bg-popover px-2.5 py-2 text-xs shadow-md">
                    <div className="mb-1.5 font-medium text-foreground">{formatBucket(Number(label), stepMs)}</div>
                    <div className="flex flex-col gap-1">
                      {visible.map((s) => {
                        const value = payload.find((p) => p.dataKey === s.key)?.value;
                        return (
                          <div key={s.key} className="flex items-center gap-2">
                            <Swatch color={s.color} type={s.type} dashed={s.dashed} />
                            <span className="flex-1 text-muted-foreground">{s.label}</span>
                            <span className="font-mono tabular-nums text-foreground">
                              {typeof value === "number" ? format(value) : DASH}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
            />
            {referenceLines?.map((line) => (
              <ReferenceLine
                key={`${line.y}-${line.label}`}
                y={line.y}
                stroke={line.color ?? "hsl(var(--muted-foreground))"}
                strokeDasharray="4 4"
                strokeOpacity={0.7}
                label={line.label ? { value: line.label, position: "insideTopRight", fontSize: 10,
                  fill: "hsl(var(--muted-foreground))" } : undefined}
              />
            ))}
            {visible.map((s) => {
              if (s.type === "bar") {
                const top = stackTops.get(s.stackId ?? s.key) === s.key;
                return (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    stackId={s.stackId}
                    fill={s.color}
                    radius={top ? [3, 3, 0, 0] : 0}
                    maxBarSize={22}
                    stroke="hsl(var(--card))"
                    strokeWidth={s.stackId ? 1 : 0}
                    isAnimationActive={false}
                  />
                );
              }
              if (s.type === "area") {
                return (
                  <Area
                    key={s.key}
                    dataKey={s.key}
                    stackId={s.stackId}
                    type={s.step ? "stepAfter" : "linear"}
                    stroke={s.color}
                    strokeWidth={1.5}
                    fill={s.color}
                    fillOpacity={0.18}
                    connectNulls={false}
                    dot={false}
                    activeDot={{ r: 3, strokeWidth: 0 }}
                    isAnimationActive={false}
                  />
                );
              }
              return (
                <Line
                  key={s.key}
                  dataKey={s.key}
                  type={s.step ? "stepAfter" : "linear"}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray={s.dashed ? "4 3" : undefined}
                  dot={false}
                  activeDot={{ r: 3.5, strokeWidth: 2, stroke: "hsl(var(--card))" }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              );
            })}
          </ComposedChart>
        </ChartContainer>
        {!hasData && emptyText && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center pb-6 text-xs text-muted-foreground">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
}

function Swatch({ color, type, dashed }: { color: string; type?: ChartSeries["type"]; dashed?: boolean }) {
  if (type === "bar" || type === "area") {
    return <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ backgroundColor: color }} />;
  }
  return (
    <span
      className="inline-block w-3.5 shrink-0"
      style={{ borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}` }}
    />
  );
}
